import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpRequest } from '@angular/common/http';
import { AuthInfo } from "./AuthInfo";
import { map, tap, filter } from 'rxjs/operators';
import { environment } from "../environment.prod";
import { Subject, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { HubConnectionBuilder, LogLevel, HubConnection } from '@aspnet/signalr';
import { UiEventService } from './ui-event.service';

@Injectable({ providedIn: "root" })
export class ApiService {

  userFirstName: string;

  userLastName: string;

  userEMail: string;

  userImageUri: string;

  userToken: string;

  isUserLoggedIn: boolean = false;

  isPlatformAdmin = false;

  scopeId: string;

  dashboardId: string;

  dashboardSecret: string;

  pageTitle: string;

  brandColor: string;

  brandColorDefault: string = '#003366';

  constructor(
    private http: HttpClient,
    private router: Router,
    private uiEvents: UiEventService
  ) {
    this.loadAuth();
  }

  private getUri(res: string, method: string) {
    // Specify the api url in environment.ts / environment.prod.ts
    return environment.api + '/' + res;
  }

  appendAuthHeader(headers: HttpHeaders): HttpHeaders {

    var h = headers.append('Accept', 'application/json');

    if (this.userEMail && this.userToken)
      h = h.append('AppAuthorization', this.userEMail + ' ' + this.userToken);

    return h;
  }

  getHeaders(): HttpHeaders {

    var headers = this.appendAuthHeader(new HttpHeaders());
    return headers;
  }

  getRequestOptions() {
    var options = {
      headers: this.getHeaders()
    };

    return options;
  }

  register(args: any) {
    var uri = this.getUri('register', 'POST');

    return this.http.post(uri, args).pipe(
      tap((response: any) => {
        this.handleLogin(response);
      })
    );
  }

  login(eMail: string, password: string) {

    console.log('logging in');
    var uri = this.getUri('login', 'POST');
    var data = { EMail: eMail, Password: password };

    return this.http.post(uri, data).pipe(
      tap((response: any) => {
        this.handleLogin(response);
      })
    );
  }

  changePassword(oldPassword: string, newPassword: string) {

    var uri = this.getUri('account/changepassword', 'POST');
    var data = { NewPassword: newPassword, Password: oldPassword };
    var response = this.http.post(uri, data, this.getRequestOptions());

    return response.pipe(
      tap((response: any) => {
        this.handleLogin(response);
      })
    );
  }

  forcechangePassword(newPassword: string, isForcePassword: boolean) {

    var uri = this.getUri('account/forcechangepassword', 'POST');
    var data = { NewPassword: newPassword, IsForcePassword: isForcePassword };
    var response = this.http.post(uri, data, this.getRequestOptions());

    return response.pipe(
      tap((response: any) => {
        this.handleLogin(response);
      })
    );
  }

  handleLogin(response) {
    console.log('handling login');
    if (!response.Succeeded) return;

    this.userEMail = response.EMail;
    this.userToken = response.Token;
    this.userFirstName = response.FirstName;
    this.userLastName = response.LastName;
    this.isUserLoggedIn = true;
    this.isPlatformAdmin = response.IsPlatformAdmin;

    this.saveAuth();
    console.log('logged in');
  }

  logout() {

    var logout$ = new Observable((observer) => {
      observer.next();
      observer.complete();
    });

    return logout$.pipe(tap((response: any) => {

      this.userEMail = "";
      this.userToken = "";
      this.userFirstName = "";
      this.userLastName = "";
      this.userImageUri = "";

      this.isUserLoggedIn = false;
      this.saveAuth();

      console.log('Cleared user account data');

    }));
  }

  saveAuth() {

    var authInfo = new AuthInfo();
    authInfo.eMail = this.userEMail;
    authInfo.token = this.userToken;
    authInfo.firstName = this.userFirstName;
    authInfo.lastName = this.userLastName;
    authInfo.scopeId = this.scopeId;
    authInfo.isPlatformAdmin = this.isPlatformAdmin;

    var json = JSON.stringify(authInfo);
    localStorage.setItem("auth", json);
  }

  loadAuth() {

    var json = localStorage.getItem("auth");

    if (!json) {
      this.isUserLoggedIn = false;
      return;
    }

    var auth = new AuthInfo;
    Object.assign(auth, JSON.parse(json));

    this.userEMail = auth.eMail;
    this.userToken = auth.token;
    this.userFirstName = auth.firstName;
    this.userLastName = auth.lastName;
    this.scopeId = auth.scopeId;
    this.isPlatformAdmin = auth.isPlatformAdmin;

    if (this.userEMail && this.userToken) this.isUserLoggedIn = true;
    else this.isUserLoggedIn = false;
  }

  changeScope(id) {
    console.log('changing scope id');
    this.scopeId = id;
    this.saveAuth();
    this.uiEvents.publish("scopechanged", id);
  }

  changeSetDashboard(d) {
    console.log('setting dashboard');
    this.dashboardId = d.Id;
    this.dashboardSecret = d.Secret;
  }

  get<T>(res) {
    var uri = this.getUri(res, 'GET');
    return this.http.get<T>(uri, this.getRequestOptions());
  }

  post(res, data) {
    var uri = this.getUri(res, 'POST');
    return this.http.post(uri, data, this.getRequestOptions());
  }

  put(res, data) {
    var uri = this.getUri(res, 'PUT');
    return this.http.put(uri, data, this.getRequestOptions());
  }

  delete(res) {
    var uri = this.getUri(res, "DELETE");
    return this.http.delete(uri, this.getRequestOptions());
  }

  request<T>(request: HttpRequest<T>) {
    var currentHeaders = request.headers;
    var res = request.url;
    var uri = this.getUri(res, request.method);
    var modifiedHeaders = currentHeaders ? this.appendAuthHeader(currentHeaders) : this.getHeaders();
    var modifiedRequest = request.clone({ url: uri, headers: modifiedHeaders });
    return this.http.request(modifiedRequest);
  }


  private _connection: HubConnection;

  onEvent$: Subject<any> = new Subject<any>();

  public subscribeToEventChannel(channel: string): Observable<any> {
    this.connectToHub().then(() => {
      this._connection.invoke('subscribe', channel);
    });

    return this.onEvent$.pipe(filter(e => e.Channel === channel)).pipe(map(e => e.Payload));
  }

  private _isConnectionStarted = false;
  private _onConnectionStarted: Promise<void>;

  private connectToHub(): Promise<void> {

    if (!this._onConnectionStarted) {

      var uri = '/event?a=1';

      if (this.userEMail) uri += "&eMail=" + this.userEMail;
      if (this.userToken) uri += "&token=" + this.userToken;
      if (this.dashboardId) uri += "&dashboardId=" + this.dashboardId;
      if (this.dashboardSecret) uri += "&dashboardSecret=" + this.dashboardSecret;


      this._connection = new HubConnectionBuilder()
        .withUrl(uri)
        .configureLogging(LogLevel.Information)
        .build();

      this._connection.on("BroadcastEvent", d => {
        this.onEvent$.next(d);
      });

      this._onConnectionStarted = this._connection.start()
        .then(() => {
          this._isConnectionStarted = true;
        })
        .catch(err => console.error(err.toString()));
    }

    if (!this._isConnectionStarted)
      return this._onConnectionStarted;

    return new Promise(function (resolve, reject) { resolve(); });
  }
}
